<script lang="ts" setup>
import { useRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import { useCardCustomizationStyle } from '@consumer/modules/recipientGiftLandingV2/cardTheming'
import newMiscGiftlyLogoIcon from '@consumer/assets/icons/new-misc/giftly-logo.svg?svgicon'
import EnvelopeScene from './EnvelopeScene.vue'
import ReadingStage, { type ReadingBeat } from './ReadingStage.vue'
import ShowcaseRedemptionStage from './ShowcaseRedemptionStage.vue'
import NavBar from './NavBar.vue'

// Guided, narrated walkthrough of the greeting-card recipient experience,
// reusing the real recipient-flow visuals (envelope → reading stage → folio
// redemption) but paced by the NavBar rather than the recipient page's
// auto-timers. Two modes:
//   demo    — generic sample gift, 10 beats, teaches a first-time visitor.
//   preview — the buyer's actual gift, 9 beats (drops the thank-you), with
//             recipient-specific narration.
// The gift is provided via useRecipientLanding() (always in preview mode, so
// nothing here touches the backend).
const { mode } = defineProps<{ mode: 'demo' | 'preview' }>()

const emit = defineEmits<{ close: [] }>()

const {
  gift,
  recipientName,
  senderName,
  messageForRecipient,
} = $(useRecipientLanding())

// The demo is a generic walkthrough, so it addresses the card generically
// ("From Me" / "To You"). This also covers the gift-page preview, which runs in
// demo mode. Every other preview (the cart or buyer page) shows the buyer's
// real gift, so it uses the greeting's from/to and stays blank when they're
// unset — no generic placeholder.
const displaySender = $computed(() => mode === 'demo' ? 'Me' : (senderName || ''))
const displayRecipient = $computed(() => mode === 'demo' ? 'You' : (recipientName || ''))

const cardCustomizationStyle = $(useCardCustomizationStyle(() => gift))

const storySlides = $computed(() => gift.greeting?.greetingCardCustomization?.storySlides ?? [])
// Prefer the inlined data URI (the demo/preview fixtures ship a bundled sample
// recording there); fall back to a real attachment for any other caller.
const voiceMemo = $computed(() =>
  gift.greeting?.greetingCardCustomization?.base64VoiceMemo
  ?? gift.greeting?.greetingCardCustomization?.voiceMemo)
const previewImageUrl = $computed(() => gift.greeting?.card?.previewImageUrl ?? null)
const fullPageImageUrl = $computed(() => gift.greeting?.card?.fullPageImageUrl ?? null)

// ===== Narration + labels, keyed by mode =====
// Reading narration is keyed off the beat *kind* (not an index) so it follows
// whatever pages actually exist (voice memo / photos are optional). The demo
// teaches every beat; the preview shows no reading narration at all — the
// buyer's own card, note, voice, and photo content speaks for itself.
const READING_NARRATION_DEMO: Record<ReadingBeat, string> = {
  cover: 'Your gift is delivered in a greeting card you can personalize',
  note: 'You can include a written message.\nTell them why you\'re thinking of them.',
  'voice-memo': 'You can also add a voice memo.\nMaybe sing them a song or share a story.',
  photo: 'Add a photo with a caption.\nBring back a favorite memory.',
}

const READING_NARRATION_PREVIEW: Record<ReadingBeat, string> = {
  cover: '',
  note: '',
  'voice-memo': '',
  photo: '',
}

const config = $computed(() => {
  const who = recipientName || 'They'
  if (mode === 'preview') {
    return {
      introNarration: 'Here\'s the gifting experience your recipient will receive',
      introCta: 'Start Gift Preview',
      readingNarration: READING_NARRATION_PREVIEW,
      revealNarration: {
        7: '',
        8: 'Your gift includes a Gift Guide with Gift Tips for each included business.',
        9: `${who} will choose how\nthey'd like to redeem their gift.`,
      } as Record<number, string>,
      // Beat 7 (the closed giftly) has no button — it auto-advances to 8.
      revealCta: { 7: '', 8: 'How To Redeem', 9: 'Close Preview' } as Record<number, string>,
      lastBeat: 9,
    }
  }
  return {
    introNarration: 'Preview an example of the delightful gifting experience your recipient will enjoy',
    introCta: 'View Gift Demo',
    readingNarration: READING_NARRATION_DEMO,
    revealNarration: {
      7: '',
      8: 'Every gift includes a Gift Guide with Gift Tips to ensure they get the most from their gift',
      9: 'They\'ll choose how\nthey\'d like to redeem their gift',
      10: 'Then they\'ll go and use the gift to get exactly what they want',
    } as Record<number, string>,
    // Beat 7 (the closed giftly) has no button — it auto-advances to 8.
    revealCta: { 7: '', 8: 'How To Redeem', 9: 'Next', 10: 'Close' } as Record<number, string>,
    lastBeat: 10,
  }
})

// ===== Stage machine =====
// intro → opening → fading (envelope opens) → reading → redemption.
type Stage = 'intro' | 'opening' | 'fading' | 'reading' | 'redemption'
let stage = $ref<Stage>('intro')
let readingBeat = $ref<ReadingBeat>('cover')
let revealBeat = $ref(7)

const onReadingBeat = (beat: ReadingBeat) => { readingBeat = beat }

// Envelope-open choreography timing — mirrors StaticAnimatedPage /
// EnvelopeScene (flap opens ~2.6s, card fully revealed ~3.2s).
const FADE_AT_MS = 2600
const READING_AT_MS = 3200

const timeouts: ReturnType<typeof setTimeout>[] = []
const schedule = (fn: () => void, ms: number) => timeouts.push(setTimeout(fn, ms))
const clearTimers = () => timeouts.splice(0).forEach(clearTimeout)
onBeforeUnmount(clearTimers)

function openEnvelope () {
  stage = 'opening'
  schedule(() => { stage = 'fading' }, FADE_AT_MS)
  schedule(() => { stage = 'reading' }, READING_AT_MS)
}

// The closed-giftly beat (7) is a transient "your gift is opening" frame with
// no next button — hold it briefly (long enough for the folio to emerge from
// the envelope) then auto-open into beat 8.
const BEAT_7_HOLD_MS = 1500

function startRedemption () {
  clearTimers()
  revealBeat = 7
  stage = 'redemption'
  schedule(() => { if (revealBeat === 7) revealBeat = 8 }, BEAT_7_HOLD_MS)
}

const narration = $computed(() => {
  if (stage === 'reading') return config.readingNarration[readingBeat as ReadingBeat]
  if (stage === 'redemption') return config.revealNarration[revealBeat] ?? ''
  return config.introNarration
})

const navNextLabel = $computed(() =>
  stage === 'redemption' ? config.revealCta[revealBeat] ?? 'Next' : config.introCta,
)

const canGoBack = $computed(() => stage === 'redemption')

function onNext () {
  if (stage === 'intro') return openEnvelope()
  if (stage === 'redemption') {
    if (revealBeat < config.lastBeat) revealBeat += 1
    else emit('close')
  }
}

function onBack () {
  if (stage !== 'redemption') return
  clearTimers() // cancel any pending beat-7 auto-advance
  // Beat 7 is transient (no stop of its own), so stepping back from 8 returns
  // straight to the reading stage rather than the closed folio.
  if (revealBeat > 8) revealBeat -= 1
  else stage = 'reading'
}
</script>

<template>
  <div class="gift-showcase-flow" :style="cardCustomizationStyle">
    <!-- Faint watermark — must be the FIRST child so every stage paints over
         it. (The stages are position:absolute/z-auto, so a trailing watermark
         would sit on top of them and wash everything out.) -->
    <GIcon :svg="newMiscGiftlyLogoIcon" class="background-giftly-logo"/>

    <!-- Narration pill pinned near the top of the flow, just below the solid
         modal header, horizontally centered. A single always-rendered pill,
         `v-show`n only when the current beat has narration — so beats like
         the plain-giftly reveal show no empty pill (v-show's inline
         display:none is unconditional; no orphaned transition nodes). -->
    <div v-show="narration" class="narration">{{ narration }}</div>

    <div class="stage-area">
      <EnvelopeScene
        v-show="stage === 'intro' || stage === 'opening' || stage === 'fading'"
        :play="stage === 'opening' || stage === 'fading'"
        :fadeOut="stage === 'fading'"
        :senderName="displaySender"
        :recipientName="displayRecipient"
        :previewImageUrl="previewImageUrl"
        :fullPageImageUrl="fullPageImageUrl"
      />
    </div>

    <ReadingStage
      v-if="stage === 'reading'"
      :storySlides="storySlides"
      :voiceMemo="voiceMemo"
      :messageForRecipient="messageForRecipient"
      :previewImageUrl="previewImageUrl"
      :fullPageImageUrl="fullPageImageUrl"
      :senderName="displaySender"
      openGiftLabel="Open Gift"
      @beat="onReadingBeat"
      @redeem="startRedemption"
      @backToEnvelope="stage = 'intro'"
    />

    <ShowcaseRedemptionStage
      v-else-if="stage === 'redemption'"
      :gift="gift"
      :mode="mode"
      :revealBeat="revealBeat"
      :senderName="displaySender"
    />

    <NavBar
      v-if="stage === 'intro' || stage === 'redemption'"
      :nextLabel="navNextLabel"
      :canGoBack="canGoBack"
      @next="onNext"
      @back="onBack"
    />
  </div>
</template>

<style scoped lang="scss">
// Layout CSS variables consumed by the reused stages (EnvelopeScene,
// ReadingStage, folio). Copied from StaticAnimatedPage's `.static-animated-page`
// — the color / font vars are set inline via `cardCustomizationStyle`; these
// are the layout + paint-time safety-net defaults.
.gift-showcase-flow {
  --env-height: 320px;
  --card-width: 300px;
  --card-height: 450px;
  --scene-scale: 1;
  --nav-bar-bottom: 30px;
  --nav-bar-max-width: 1024px;
  --nav-bar-reserved-space: 90px;

  // Top counterpart to --nav-bar-reserved-space: the narration pill's zone
  // (top offset + a two-line pill), reserved so the envelope/card centers
  // between the narration and the navbar rather than under the narration.
  --narration-reserved-space: 100px;

  --bg-color: #eee5c0;
  --env-color: #ffb700;
  --env-border-width: 12px;
  --env-text-color: #fff;
  --env-shadow: #5a1a10;
  --card-color: #fffcf9;
  --text-dark: #4a3e3d;
  --text-light: #9c8e8d;
  --stamp-color: #ff8b94;
  --redemption-bg-color: #e7b8c6;
  --card-envelope-box-shadow: 0 33px 42.8px 0 rgb(0 0 0 / 19%);

  align-items: center;
  background-color: var(--bg-color);
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: 0 16px;
  position: relative;
  width: 100%;
}

// Centered translucent pill pinned near the top of the flow, just below the
// solid modal header — matches the design's guided-tour caption. `top` places
// it in the band under the header; the dark pill keeps the white copy legible
// over any card background.
.narration {
  align-items: center;

  // Dark translucent pill (matching the NavBar glass buttons) so the white
  // copy stays legible over any card background.
  backdrop-filter: blur(10px);
  background: rgb(50 50 50 / 45%);
  border-radius: 60px;
  color: #fff;
  display: flex;
  font-family: Gilroy, sans-serif;
  font-size: 16px;
  font-weight: 500;
  justify-content: center;
  left: 50%;
  line-height: 140%;

  // Fixed 373px per design, capped so it never overflows a narrow viewport.
  max-width: calc(100% - 32px);
  padding: 8px 19px 8px 24px;
  position: absolute;
  text-align: center;
  top: 35px;
  transform: translateX(-50%);

  // Honor authored newlines (e.g. the redeem beat's forced break) while still
  // wrapping normally.
  white-space: pre-line;
  width: 373px;
  z-index: 31;
}

// Short screens have little headroom, so pull the pill closer to the top.
@media (max-height: 736px) {
  .narration {
    top: 10px;
  }
}

// Center the idle/opening envelope+card in the band between the narration pill
// (top) and the navbar (bottom) — reserving both zones keeps it centered rather
// than tucked up under the narration. ReadingStage / ShowcaseRedemptionStage
// are absolutely positioned and ignore this.
.stage-area {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  padding-bottom: var(--nav-bar-reserved-space);
  padding-top: var(--narration-reserved-space);

  // Passive layout container for the idle/opening envelope — the flow
  // advances via the NavBar, never by clicking the envelope. Kept
  // non-interactive so it never sits over (and swallows clicks meant for)
  // the reading-stage / redemption NavBar rendered beside it.
  pointer-events: none;
  width: 100%;
  z-index: 1;
}

.background-giftly-logo {
  color: var(--env-color);
  height: 150%;
  left: 300px;
  opacity: 0.05;

  // Decorative watermark — rendered last, so keep it click-through or it
  // swallows taps meant for the NavBar/stage beneath it.
  pointer-events: none;
  position: absolute;
  top: -30%;
  transform: scale(1.3);
  width: 160%;
  z-index: 0;

  @media (max-width: 1023px) {
    height: 150%;
    left: -25%;
    top: -30%;
    transform: scale(4.5);
    width: 150%;
  }
}
</style>
